/**
 * MyResultsPage - Display user's own analysis history
 * Requirements: 5.1, 5.2 - User's past analysis results
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { historyApi, type HistoryItem, type SessionDetail } from '../api';
import { CustomRadarChart } from './CustomRadarChart';
import { Gb74PersonalReport } from './Gb74PersonalReport';
import { unwrapGb74Scores } from '../utils/gb74Report';

export const MyResultsPage: React.FC = () => {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<SessionDetail | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await historyApi.getHistory();
      setHistory(data.items);
    } catch (err) {
      console.error('Failed to load history:', err);
      setError('履歴の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectSession = async (sessionId: string) => {
    try {
      setIsDetailLoading(true);
      const detail = await historyApi.getSessionDetail(sessionId);
      setSelectedSession(detail);
    } catch (err) {
      console.error('Failed to load session detail:', err);
      setError('詳細の取得に失敗しました');
    } finally {
      setIsDetailLoading(false);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    setDeleteTarget(sessionId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleting(true);
      await historyApi.deleteSession(deleteTarget);
      setHistory(prev => prev.filter(h => h.sessionId !== deleteTarget));
      setDeleteTarget(null);
      if (selectedSession?.sessionId === deleteTarget) {
        setSelectedSession(null);
      }
    } catch (err) {
      console.error('Failed to delete session:', err);
      setError('削除に失敗しました');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && history.length === 0) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (history.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          まだ分析結果がありません
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          質問に回答すると、ここに結果が表示されます
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={2}>
        {history.map((item) => (
          <Card key={item.sessionId} variant="outlined">
            <CardActionArea onClick={() => handleSelectSession(item.sessionId)}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {item.questionSetTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(item.completedAt)}
                    </Typography>
                    {item.analysisResult?.patternName && (
                      <Chip
                        label={item.analysisResult.patternName}
                        color="primary"
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteClick(e, item.sessionId)}
                    aria-label="削除"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>

      {/* Detail Dialog */}
      <Dialog
        open={!!selectedSession}
        onClose={() => setSelectedSession(null)}
        maxWidth="sm"
        fullWidth
      >
        {isDetailLoading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : selectedSession ? (
          (() => {
            const gb74Scores = selectedSession.analysisResult
              ? unwrapGb74Scores(selectedSession.analysisResult.scores)
              : null;

            // GB74形式は専用の個人レポートを表示 (回答内容リストは省略)
            if (gb74Scores) {
              return (
                <>
                  <DialogContent sx={{ p: 0, bgcolor: '#FAFAF8' }}>
                    <Gb74PersonalReport
                      scores={selectedSession.analysisResult!.scores}
                      userName={selectedSession.userName}
                      meta={[selectedSession.questionSetTitle, selectedSession.completedAt ? formatDate(selectedSession.completedAt) : null].filter(Boolean).join('｜')}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setSelectedSession(null)}>閉じる</Button>
                  </DialogActions>
                </>
              );
            }

            return (
              <>
                <DialogTitle>{selectedSession.questionSetTitle}</DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {selectedSession.completedAt ? formatDate(selectedSession.completedAt) : '未完了'}
                  </Typography>

                  {selectedSession.analysisResult && (
                    <Box sx={{ my: 2 }}>
                      {selectedSession.analysisResult.patternName && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h6" color="primary">
                            {selectedSession.analysisResult.patternName}
                          </Typography>
                          <Typography variant="body2">
                            {selectedSession.analysisResult.patternDescription}
                          </Typography>
                        </Box>
                      )}

                      <CustomRadarChart
                        scores={{ scores: selectedSession.analysisResult.scores }}
                        title="思考傾向スコア"
                        width={300}
                        height={300}
                      />
                    </Box>
                  )}

                  <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    回答内容
                  </Typography>
                  <Stack spacing={1}>
                    {selectedSession.answers.map((answer, index) => (
                      <Box key={answer.questionId} sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          Q{index + 1}. {answer.questionText}
                        </Typography>
                        <Typography variant="body2" color="primary">
                          A. {answer.answerChoiceText}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setSelectedSession(null)}>閉じる</Button>
                </DialogActions>
              </>
            );
          })()
        ) : null}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>履歴を削除</DialogTitle>
        <DialogContent>
          <Typography>この分析結果を削除してもよろしいですか？</Typography>
          <Typography variant="body2" color="text.secondary">
            この操作は取り消せません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
            キャンセル
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={20} /> : '削除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
